import { Box, Button, Flex, Grid, IconButton, Text, VStack } from '@chakra-ui/react';
import type { DemoInvitationDto } from '@fluenceur/types';
import { Calendar, CalendarCheck, Xmark } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { BookedNotice, BookingError, SlotsToggle, VincentAvatar } from './demo-parts';
import { DemoSlotPicker } from './DemoSlotPicker';
import { useDemoBookingFlow, type DemoBookingFlow } from './use-demo-booking-flow';

const SlotsColumn = ({ flow }: { flow: DemoBookingFlow }) => {
  const { t } = useTranslation();
  return (
    <VStack align="stretch" gap="10px">
      <Flex align="center" gap={2} fontSize="12px" fontWeight="medium" color="navInactive">
        <VincentAvatar size="24px" ringColor="white" />
        {t('demoInvitation.banner.available')}
      </Flex>
      <DemoSlotPicker flow={flow} tone="light" />
      {flow.selectedLabels ? (
        <Button
          h="40px"
          bg="gray.900"
          color="white"
          _hover={{ bg: 'navInactive' }}
          onClick={flow.book}
          loading={flow.isBooking}
        >
          <CalendarCheck width={16} height={16} />
          {t('demoInvitation.confirm')} {flow.selectedLabels.day.toLowerCase()} ·{' '}
          {flow.selectedLabels.time}
        </Button>
      ) : null}
      <BookingError flow={flow} tone="light" />
      <SlotsToggle flow={flow} tone="light" label={t('demoInvitation.allSlots')} />
    </VStack>
  );
};

/**
 * The dashboard block that takes over from the capsule on the third day: what
 * the demo brings on the left, Vincent's next slots and the booking on the right.
 */
export const DemoBanner = ({
  brandId,
  invitation,
}: {
  brandId: string;
  invitation: DemoInvitationDto;
}) => {
  const { t } = useTranslation();
  const flow = useDemoBookingFlow({ brandId, invitation, readSlots: true });

  return (
    <Grid
      as="section"
      aria-label={t('demoInvitation.ariaLabel')}
      position="relative"
      bg="lilac.300"
      borderRadius="2xl"
      px="26px"
      py={6}
      templateColumns={{ base: '1fr', lg: 'minmax(0, 1fr) auto' }}
      gap={{ base: 5, lg: 8 }}
      alignItems="center"
    >
      <IconButton
        aria-label={t('common.close')}
        position="absolute"
        top="14px"
        right="14px"
        size="xs"
        variant="outline"
        borderRadius="full"
        borderColor="rgba(26,26,26,.18)"
        color="gray.900"
        _hover={{ bg: 'rgba(255,255,255,.4)' }}
        onClick={flow.close}
      >
        <Xmark width={15} height={15} />
      </IconButton>

      <VStack align="stretch" gap="10px" minW={0}>
        <Flex
          alignSelf="flex-start"
          h="30px"
          px="13px"
          borderRadius="full"
          bg="gray.900"
          color="navDark.aiText"
          align="center"
          gap="7px"
          fontFamily="heading"
          fontSize="13px"
          fontWeight="semibold"
        >
          <Calendar width={14} height={14} />
          {t('demoInvitation.banner.tag')}
        </Flex>
        <Text
          fontFamily="heading"
          fontSize="22px"
          fontWeight="semibold"
          letterSpacing="-0.02em"
          lineHeight="1.15"
          textWrap="balance"
        >
          {t('demoInvitation.banner.title')}
        </Text>
        <Text fontSize="14px" lineHeight="1.5" color="navInactive" maxW="460px" textWrap="pretty">
          {t('demoInvitation.banner.description')}
        </Text>
      </VStack>

      <Box w={{ base: 'full', lg: '300px' }} mr={{ lg: 6 }}>
        {flow.booking ? <BookedNotice flow={flow} tone="light" /> : <SlotsColumn flow={flow} />}
      </Box>
    </Grid>
  );
};
